import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { send, greeting } from './agentClient';
import useCatalog from '../hooks/useCatalog';

// Conversation state for Raano AI (design-system.md §9.3). Messages are { id, role: 'user'|'assistant', blocks }.
const AgentContext = createContext();

let nextId = 1;
const id = () => nextId++;

export const AgentProvider = ({ children }) => {

    const navigate = useNavigate();
    const location = useLocation();
    const catalog = useCatalog();
    const [open, setOpen] = useState(false);
    const [expanded, setExpanded] = useState(false);
    const [messages, setMessages] = useState([]);
    const [status, setStatus] = useState('idle'); // idle | thinking
    const [cartDot, setCartDot] = useState(false);
    const cancelled = useRef(false);

    // Product page context: /product/:id
    const productId = location.pathname.startsWith('/product/') ? location.pathname.split('/')[2] : null;
    const product = catalog.products.find(p => p._id === productId) || null;
    const context = { page: location.pathname, product };

    const run = useCallback(async (text) => {
        setStatus('thinking');
        cancelled.current = false;
        const blocks = await send({ text, context, catalog });
        if (cancelled.current) return;
        setStatus('idle');
        const action = blocks.find(b => b.type === 'action' && b.name === 'goto');
        setMessages(m => [...m, { id: id(), role: 'assistant', blocks: blocks.filter(b => b.type !== 'action') }]);
        if (action) setTimeout(() => { setOpen(false); navigate(action.to); }, 900);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [catalog, product, location.pathname]);

    const sendText = useCallback((text) => {
        const t = text.trim();
        if (!t || status === 'thinking') return;
        setMessages(m => [...m, { id: id(), role: 'user', blocks: [{ type: 'text', markdown: t }] }]);
        run(t);
    }, [run, status]);

    const stop = () => { cancelled.current = true; setStatus('idle'); };

    // Opening fresh: greet, or (on a product page) ask about that product
    const openAgent = () => {
        setOpen(true);
        if (messages.length) return;
        const start = greeting(context);
        const seeded = start.find(b => b.type === 'user');
        if (seeded) {
            setMessages([{ id: id(), role: 'user', blocks: [{ type: 'text', markdown: seeded.text }] }]);
            run(seeded.text);
        } else {
            setMessages([{ id: id(), role: 'assistant', blocks: start }]);
        }
    };

    const reset = () => { cancelled.current = true; setStatus('idle'); setMessages([]); setCartDot(false); };

    // A new product page starts a new conversation next time it opens
    useEffect(() => { if (!open) setMessages([]); }, [productId]); // eslint-disable-line react-hooks/exhaustive-deps

    const value = { open, openAgent, close: () => setOpen(false), expanded, setExpanded, messages, status, sendText, stop, reset, context, catalog, cartDot, setCartDot };
    return <AgentContext.Provider value={value} >{children}</AgentContext.Provider>;
};

export const useAgent = () => useContext(AgentContext);
